/**
 * Terms for the /glossary page. Each entry becomes an anchored row on the
 * page, so `id` is a public URL fragment (/glossary#bounce-rate) — treat ids
 * as stable once published. Definitions stay generic (category explainers,
 * not product copy); the optional `link` points at the library post or
 * metrics page that goes deeper.
 */
export type GlossaryTerm = {
  /** Anchor id — lowercase-hyphenated, stable once published. */
  id: string;
  term: string;
  /** 1–3 plain-English sentences. */
  definition: string;
  /** "Read more" target — an internal library/metrics/guides path. */
  link?: { label: string; to: string };
  /** Optional product-page target when Bigdelta has a feature page for the term. */
  productLink?: { label: string; to: string };
};

export type GlossaryLetterGroup = {
  letter: string;
  terms: GlossaryTerm[];
};

const TERMS: GlossaryTerm[] = [
  // A
  {
    id: "a-b-testing",
    term: "A/B testing",
    definition:
      "Showing two versions of a page or element to different visitors at random and measuring which one converts better. The test runs until the difference is large enough to trust, then the winner ships to everyone.",
  },
  {
    id: "activation-rate",
    term: "Activation rate",
    definition:
      "The share of new signups who reach the moment your product delivers its first real value — sending the first message, publishing the first page. It separates people who tried the product from people who merely created an account.",
    link: { label: "Activation rate", to: "/metrics/activation-rate" },
  },
  {
    id: "active-users",
    term: "Active users (DAU, WAU, MAU)",
    definition:
      "People who used your product within a given window: a day (DAU), a week (WAU), or a month (MAU). DAU divided by MAU gives the stickiness ratio — how much of your monthly audience shows up on a typical day.",
    link: { label: "DAU/MAU (Stickiness Ratio)", to: "/metrics/dau-mau-stickiness" },
  },
  {
    id: "ad-blocker",
    term: "Ad blocker",
    definition:
      "A browser extension or built-in browser feature that blocks ads — and, as a side effect, most client-side analytics scripts. Visitors with blockers browse your site without ever appearing in your numbers.",
    link: {
      label: "Dark traffic: the visitors your analytics can't see",
      to: "/blog/dark-traffic",
    },
  },
  {
    id: "arr",
    term: "Annual recurring revenue (ARR)",
    definition:
      "Recurring subscription revenue normalized to a twelve-month period, usually calculated as MRR × 12. Companies selling annual contracts tend to quote ARR as their headline growth number.",
    link: { label: "Annual Recurring Revenue (ARR)", to: "/metrics/arr" },
  },
  {
    id: "anonymous-visitor",
    term: "Anonymous visitor",
    definition:
      "A visitor your analytics can count but not name. Tools can tell you their country, device, and what they did — identifying a stranger personally isn't possible until they sign up or otherwise tell you who they are.",
    link: {
      label: "Who is visiting my website?",
      to: "/blog/who-is-visiting-my-website",
    },
  },
  {
    id: "attribution",
    term: "Attribution",
    definition:
      "Deciding which marketing touchpoint gets credit for a conversion. Models range from last-click, where the final touch takes everything, to multi-touch, where credit is spread across the journey — and different models produce very different reports from the same data.",
    link: {
      label: "Why your analytics numbers never match: GA4 vs ads platforms",
      to: "/blog/analytics-vs-ad-platforms",
    },
  },
  {
    id: "average-session-duration",
    term: "Average session duration",
    definition:
      "The mean length of a visit, measured from first to last recorded interaction. The metric is shakier than it looks: time on the final page usually can't be measured, so real visits are longer than the number suggests.",
    link: {
      label: "Average session duration is lying to you",
      to: "/blog/average-session-duration",
    },
  },

  // B
  {
    id: "behavioral-analytics",
    term: "Behavioral analytics",
    definition:
      "Analysis centered on what people do — the pages they view, the buttons they click, the paths they follow — rather than who they are demographically. Funnels, cohorts, and session replay all fall under this umbrella.",
    link: {
      label: "What is website behavior analytics?",
      to: "/blog/website-behavior-analytics",
    },
  },
  {
    id: "bot-traffic",
    term: "Bot traffic",
    definition:
      "Visits from automated software: search engine crawlers, scrapers, uptime monitors, spam bots. Left unfiltered, bots inflate visitor counts and drag down engagement metrics, since they rarely behave like people.",
  },
  {
    id: "bounce-rate",
    term: "Bounce rate",
    definition:
      "The share of sessions that end without a second page view or meaningful interaction. What counts as a bounce varies by tool — GA4, for instance, defines it as the inverse of engagement rate — so compare numbers across tools with care.",
    link: { label: "What is a good bounce rate?", to: "/blog/good-bounce-rate" },
  },

  // C
  {
    id: "campaign-tracking",
    term: "Campaign tracking",
    definition:
      "Tagging the links you share — in ads, emails, social posts — so analytics can tie each visit back to the campaign that produced it. In practice this usually means UTM parameters.",
    link: { label: "UTM parameters: a practical guide", to: "/blog/utm-parameters-guide" },
  },
  {
    id: "checkout-abandonment",
    term: "Checkout abandonment",
    definition:
      "When a shopper starts the checkout process but leaves before paying. Abandonment rate is the share of started checkouts that never finish, and it's one of the highest-leverage numbers in e-commerce.",
    link: {
      label: "Checkout abandonment rate: benchmarks and how to reduce it",
      to: "/blog/checkout-abandonment-rate",
    },
  },
  {
    id: "churn-rate",
    term: "Churn rate",
    definition:
      "The percentage of customers who cancel during a period, measured against how many you started with. Its mirror image is retention rate; together they tell you whether the business keeps what it wins.",
    link: { label: "Customer Churn Rate", to: "/metrics/customer-churn-rate" },
  },
  {
    id: "click-map",
    term: "Click map",
    definition:
      "A heatmap variant that overlays a page with the spots visitors click most. Useful for spotting ignored calls to action — and clicks on things that aren't actually clickable.",
    link: {
      label: "What is a click map?",
      to: "/blog/what-is-a-click-map",
    },
  },
  {
    id: "clickstream",
    term: "Clickstream",
    definition:
      "The ordered trail of pages and clicks a visitor follows. Your own analytics records it first-party on your site; third-party panels collect trails across the web and sell them in aggregate — the raw material behind competitor traffic estimates.",
    link: {
      label: "What is clickstream data?",
      to: "/blog/clickstream-data",
    },
  },
  {
    id: "client-side-tracking",
    term: "Client-side tracking",
    definition:
      "Collecting analytics with a script that runs in the visitor's browser and reports events directly. It's the default approach for most tools, and the one ad blockers and privacy browsers interfere with.",
    link: {
      label: "Server-side vs client-side tracking, explained without jargon",
      to: "/blog/server-side-vs-client-side",
    },
  },
  {
    id: "cohort-analysis",
    term: "Cohort analysis",
    definition:
      "Grouping users by a shared starting point — usually signup week or month — and following each group's behavior over time. It's the standard way to read retention, because it separates product changes from the noise of a growing user base.",
  },
  {
    id: "consent-banner",
    term: "Consent banner",
    definition:
      "The prompt asking visitors to accept or decline cookies, required in many jurisdictions before cookie-based tracking can start. Every visitor who declines becomes invisible to tools that depend on cookies.",
    link: {
      label: "Cookieless analytics: how counting without cookies actually works",
      to: "/blog/cookieless-analytics",
    },
  },
  {
    id: "conversion",
    term: "Conversion",
    definition:
      "A visitor completing the action your site exists for — a purchase, a signup, a booking, a submitted form. What counts as a conversion is up to you; picking the right one is half the work of measuring anything.",
    link: {
      label: "How to track website conversions",
      to: "/blog/how-to-track-website-conversions",
    },
  },
  {
    id: "conversion-funnel",
    term: "Conversion funnel",
    definition:
      "The ordered steps a visitor moves through on the way to converting — product page, cart, checkout, purchase. Fewer people complete each step than started it, so the chart narrows like a funnel; the step where it narrows fastest is where to look.",
    link: {
      label: "What is a conversion funnel?",
      to: "/blog/conversion-funnel-explained",
    },
  },
  {
    id: "conversion-rate",
    term: "Conversion rate",
    definition:
      "Conversions divided by visitors (or sessions), expressed as a percentage. A 2% conversion rate means 2 of every 100 visitors did the thing you wanted.",
    link: {
      label: "What's a good conversion rate?",
      to: "/blog/whats-a-good-conversion-rate",
    },
  },
  {
    id: "cookie",
    term: "Cookie",
    definition:
      "A small piece of data a website stores in the browser, used by analytics to recognize a returning visitor. First-party cookies are set by the site you're on; third-party cookies are set by other domains and are now blocked by most browsers.",
    link: {
      label: "Cookieless analytics: how counting without cookies actually works",
      to: "/blog/cookieless-analytics",
    },
  },
  {
    id: "cookieless-analytics",
    term: "Cookieless analytics",
    definition:
      "Analytics that counts visitors without storing cookies or other identifiers in the browser, typically by working from anonymized request data. The trade-off is precision on returning-visitor counts in exchange for privacy and no consent banner.",
    link: {
      label: "Cookieless analytics: how counting without cookies actually works",
      to: "/blog/cookieless-analytics",
    },
  },
  {
    id: "cross-domain-tracking",
    term: "Cross-domain tracking",
    definition:
      "Keeping a single session intact when a visitor moves between your domains — say, from your marketing site to a checkout hosted elsewhere. Without it, the same person shows up as two unrelated visits.",
  },
  {
    id: "customer-acquisition-cost",
    term: "Customer acquisition cost (CAC)",
    definition:
      "What you spend, on average, to win one new paying customer: sales and marketing costs divided by new customers acquired. It only means something next to lifetime value and payback period.",
    link: {
      label: "Customer Acquisition Cost (CAC)",
      to: "/metrics/customer-acquisition-cost",
    },
  },
  {
    id: "customer-lifetime-value",
    term: "Customer lifetime value (LTV)",
    definition:
      "An estimate of the total revenue one customer generates before they leave. It sets the ceiling on what you can afford to spend acquiring them.",
    link: {
      label: "Customer Lifetime Value (cLTV)",
      to: "/metrics/customer-lifetime-value",
    },
  },

  // D
  {
    id: "dark-traffic",
    term: "Dark traffic",
    definition:
      "Visits your analytics either can't see at all — blocked by ad blockers or declined consent — or can't attribute, so they get lumped into direct traffic. Every site has some; the question is how much.",
    link: {
      label: "Dark traffic: the visitors your analytics can't see",
      to: "/blog/dark-traffic",
    },
  },
  {
    id: "dashboard",
    term: "Dashboard",
    definition:
      "A single screen that collects the metrics you check regularly — traffic, conversions, revenue — so you don't have to assemble the picture report by report.",
    link: {
      label: "What is a website analytics dashboard?",
      to: "/blog/website-analytics-dashboard",
    },
  },
  {
    id: "data-layer",
    term: "Data layer",
    definition:
      "A structured JavaScript object on a page that holds information — product IDs, prices, user state — for tag managers and analytics scripts to read. It decouples what the page knows from how each tool consumes it.",
  },
  {
    id: "device-fingerprinting",
    term: "Device fingerprinting",
    definition:
      "Recognizing a returning browser by its combination of characteristics — screen size, fonts, timezone — instead of a stored cookie. Regulators treat it like any other tracking identifier, so it generally needs the same consent cookies do.",
  },
  {
    id: "digital-analytics",
    term: "Digital analytics",
    definition:
      "The umbrella term for measuring behavior across a business's digital touchpoints — website, app, email, ads, in-product activity. Web analytics, product analytics, marketing analytics, and experience analytics all sit under it.",
    link: {
      label: "What is digital analytics?",
      to: "/blog/what-is-digital-analytics",
    },
  },
  {
    id: "direct-traffic",
    term: "Direct traffic",
    definition:
      "Visits that arrive with no referrer information: someone typed the URL, used a bookmark, or came from an app or link that stripped the referrer. It's less a source than a bucket for visits whose real source is unknown.",
    link: {
      label: "What is direct traffic? (And why it's overcounted)",
      to: "/blog/what-is-direct-traffic",
    },
  },
  {
    id: "drop-off-rate",
    term: "Drop-off rate",
    definition:
      "The share of users who leave between one step of a funnel and the next. The step with the worst drop-off is usually the first thing worth investigating.",
    link: {
      label: "How to see where users get stuck on your website",
      to: "/blog/where-users-get-stuck",
    },
  },

  // E
  {
    id: "ecommerce-analytics",
    term: "Ecommerce analytics",
    definition:
      "Web analytics with orders attached: the same visits and traffic sources, joined to revenue, products, and repeat purchases. It answers which channels and pages produce buyers, not just visitors.",
    link: {
      label: "What is ecommerce analytics?",
      to: "/blog/ecommerce-analytics",
    },
  },
  {
    id: "engagement-rate",
    term: "Engagement rate",
    definition:
      "In GA4, the share of sessions that lasted over ten seconds, included a conversion, or viewed at least two pages. It replaced bounce rate as the default health metric — a bounce is now simply a session that wasn't engaged.",
    link: {
      label: "What is engagement rate? GA4's replacement for bounce rate",
      to: "/blog/engagement-rate-explained",
    },
  },
  {
    id: "event",
    term: "Event",
    definition:
      "A recorded user action: a page view, a click, a signup, a purchase. Modern analytics tools treat everything as an event, which makes choosing which ones to track the real design decision.",
    link: { label: "What events to track on your website", to: "/blog/events-to-track" },
  },
  {
    id: "event-properties",
    term: "Event properties",
    definition:
      "Details attached to an event — the plan selected, the button's label, the page it happened on. Properties are what let you slice one event a dozen ways later instead of tracking a dozen separate events.",
  },
  {
    id: "exit-page",
    term: "Exit page",
    definition:
      "The last page someone views before their session ends. High exits from a checkout step are a problem; high exits from an order-confirmation page are the point.",
    link: {
      label: "Exit rate vs bounce rate: what's the difference?",
      to: "/blog/exit-rate-vs-bounce-rate",
    },
  },
  {
    id: "exit-rate",
    term: "Exit rate",
    definition:
      "Of all views a page received, the share that were the final view of a session. Unlike bounce rate, it doesn't require the page to be the visitor's first — every session exits somewhere.",
    link: {
      label: "Exit rate vs bounce rate: what's the difference?",
      to: "/blog/exit-rate-vs-bounce-rate",
    },
  },

  // F
  {
    id: "first-party-data",
    term: "First-party data",
    definition:
      "Data you collect directly from your own audience on your own properties — analytics events, signups, purchase history. As third-party data dries up under privacy regulation, it's the data that remains dependable.",
  },
  {
    id: "form-abandonment",
    term: "Form abandonment",
    definition:
      "Starting to fill in a form and leaving without submitting it. Roughly half the people who start a form never send it, which makes the form itself one of the most common places a site loses signups and leads.",
    link: {
      label: "Form abandonment: why visitors quit your forms",
      to: "/blog/form-abandonment",
    },
  },
  {
    id: "form-analytics",
    term: "Form analytics",
    definition:
      "Field-level measurement of how people move through forms: where they hesitate, which fields trigger errors, where they give up. Forms fail one field at a time, and page-level metrics can't see which one.",
    link: {
      label: "Form abandonment: why visitors quit your forms",
      to: "/blog/form-abandonment",
    },
  },
  {
    id: "funnel-analysis",
    term: "Funnel analysis",
    definition:
      'Breaking a goal into ordered steps — landing page, signup, checkout, payment — and measuring how many people survive each one. It turns "conversion is low" into "we lose 60% of people at step three."',
    link: {
      label: "What is a conversion funnel?",
      to: "/blog/conversion-funnel-explained",
    },
  },

  // G
  {
    id: "gdpr",
    term: "GDPR",
    definition:
      "The EU's General Data Protection Regulation, in force since 2018. For analytics, its practical effect is that cookie-based tracking of EU visitors requires informed consent, and personal data collection must be minimized and justified.",
    link: {
      label: "Is session replay GDPR-compliant? A practical checklist",
      to: "/blog/session-replay-gdpr",
    },
  },
  {
    id: "goal",
    term: "Goal",
    definition:
      "A conversion target defined inside an analytics tool — reach this page, fire this event — so the tool can report a conversion rate against it. Different tools call it a goal, a key event, or simply a conversion.",
  },
  {
    id: "google-analytics-4",
    term: "Google Analytics 4 (GA4)",
    definition:
      "Google's current analytics platform, which replaced Universal Analytics in 2023. It models everything as events rather than sessions and page views, which is also why its numbers rarely match its predecessor's.",
    link: {
      label: "Why do different analytics tools show different numbers?",
      to: "/blog/why-analytics-tools-show-different-numbers",
    },
  },
  {
    id: "google-search-console",
    term: "Google Search Console",
    definition:
      "Google's free tool reporting how a site performs in its search results: impressions, clicks, average position, and indexing health. It measures on Google's side, before the click — the complement to analytics, which starts after the visitor arrives.",
    link: {
      label: "Search Console vs Google Analytics: which numbers should you trust?",
      to: "/blog/search-console-vs-google-analytics",
    },
  },

  // H
  {
    id: "heatmap",
    term: "Heatmap",
    definition:
      "A visual overlay showing where visitors click, move, and scroll on a page, with the busiest areas rendered hottest. One heatmap summarizes thousands of visits at a glance — the aggregate counterpart to watching individual session replays.",
    link: {
      label: "What is a website heatmap? The four types, explained",
      to: "/blog/what-is-a-website-heatmap",
    },
  },

  // I
  {
    id: "identity-resolution",
    term: "Identity resolution",
    definition:
      "Connecting a person's activity across devices, sessions, and the moment they sign up, so their anonymous browsing history and their known profile become one record. It's how \"visitor #48211\" becomes a customer with a first-touch source.",
    link: {
      label: "Who is visiting my website? What you can (and can't) see",
      to: "/blog/who-is-visiting-my-website",
    },
  },
  {
    id: "impression",
    term: "Impression",
    definition:
      "One display of an ad or piece of content, counted whether or not anyone clicked. Impressions measure exposure; clicks and conversions measure whether the exposure did anything.",
  },
  {
    id: "ip-anonymization",
    term: "IP anonymization",
    definition:
      "Discarding part of a visitor's IP address before it's stored, so the data can't be traced back to an individual connection. Privacy-focused analytics tools do this by default; under GDPR it's often required.",
  },

  // J
  {
    id: "javascript-snippet",
    term: "JavaScript tracking snippet",
    definition:
      "The few lines of script you paste into your site's HTML to install an analytics tool. It loads the tool's library in the visitor's browser and starts reporting events.",
  },

  // K
  {
    id: "kpi",
    term: "Key performance indicator (KPI)",
    definition:
      "One of the handful of metrics a business actually steers by, as opposed to the hundreds it could measure. If a number wouldn't change a decision, it's data, not a KPI.",
    link: {
      label: "The only 5 metrics to check every morning",
      to: "/blog/five-metrics-to-check-every-morning",
    },
  },

  // L
  {
    id: "landing-page",
    term: "Landing page",
    definition:
      "The first page of a visitor's session — where they entered your site. In marketing usage it also means a page built specifically to receive campaign traffic and convert it.",
  },

  // M
  {
    id: "micro-conversion",
    term: "Micro-conversion",
    definition:
      "A smaller action that signals progress toward the main goal: a newsletter signup, an add-to-cart, a pricing-page visit. Tracking them shows movement long before the primary conversion happens.",
  },
  {
    id: "mrr",
    term: "Monthly recurring revenue (MRR)",
    definition:
      "The predictable subscription revenue a business collects each month, and the base unit for most SaaS metrics. Growth, churn, and expansion are all usually expressed as movements in MRR.",
    link: { label: "Monthly Recurring Revenue (MRR)", to: "/metrics/mrr" },
  },

  // N
  {
    id: "nrr",
    term: "Net revenue retention (NRR)",
    definition:
      "How revenue from your existing customers changes over a period once expansion, downgrades, and churn are netted out. Above 100% means the customers you already have are worth more this year than last — growth before any new sales.",
    link: { label: "Net Revenue Retention (NRR)", to: "/metrics/nrr" },
  },
  {
    id: "new-vs-returning-visitors",
    term: "New vs. returning visitors",
    definition:
      'A split based on whether the tool has seen the visitor\'s browser before. It systematically overcounts "new": clearing cookies, switching devices, or browsing privately all turn a returning person into a new one.',
    link: {
      label: "New vs returning visitors: what the split tells you",
      to: "/blog/new-vs-returning-visitors",
    },
  },
  {
    id: "north-star-metric",
    term: "North star metric",
    definition:
      "The single metric a team treats as its best proxy for delivering value — weekly active users, orders shipped, hours watched. Its job is to align decisions, not to be the only number anyone looks at.",
  },

  // O
  {
    id: "organic-traffic",
    term: "Organic traffic",
    definition:
      "Visits from unpaid search engine results. It's the channel SEO exists to grow, and for most content sites it's the largest source of new visitors.",
    link: {
      label: "What is organic traffic?",
      to: "/blog/what-is-organic-traffic",
    },
  },

  // P
  {
    id: "page-view",
    term: "Page view",
    definition:
      "One load (or virtual load) of a page. It's the rawest traffic unit: a single person refreshing five times produces five page views, one visit, and one unique visitor.",
    link: {
      label: "Page views vs. visits vs. unique visitors",
      to: "/blog/visitors-vs-visits-vs-pageviews",
    },
  },
  {
    id: "paid-traffic",
    term: "Paid traffic",
    definition:
      "Visits from advertising — search ads, social ads, display. Analytics identifies it through link tagging, which is why untagged ads quietly leak into direct traffic.",
    link: {
      label: "Traffic sources: direct vs. organic vs. referral vs. paid",
      to: "/blog/traffic-sources-explained",
    },
  },
  {
    id: "path-analysis",
    term: "Path analysis",
    definition:
      "Mapping the sequences of pages and actions users actually take through a site, rather than the route you designed for them. The gap between the two is usually informative.",
  },
  {
    id: "product-analytics",
    term: "Product analytics",
    definition:
      "Analytics focused on what happens inside a product after signup: activation, feature usage, retention. Web analytics answers how people arrive and convert; product analytics answers whether they stay.",
    link: {
      label: "What is product analytics? A plain-English guide",
      to: "/blog/what-is-product-analytics",
    },
  },

  // Q
  {
    id: "qualitative-analytics",
    term: "Qualitative analytics",
    definition:
      "Tools that show individual behavior — session replays, heatmaps, feedback — as opposed to quantitative tools that count in aggregate. The counts tell you something is wrong; the qualitative layer shows you what it looks like.",
    link: {
      label: "Heatmaps vs. session replay: what's the difference?",
      to: "/blog/heatmaps-vs-session-replay",
    },
  },

  // R
  {
    id: "rage-click",
    term: "Rage click",
    definition:
      "Rapid repeated clicks on the same spot — the signature of a visitor expecting something to happen when it doesn't. Session replay tools flag rage clicks as a shortcut to finding broken or confusing elements.",
    link: {
      label: "Session replay, explained (with examples)",
      to: "/blog/session-replay-explained",
    },
  },
  {
    id: "real-time-analytics",
    term: "Real-time analytics",
    definition:
      "Reporting on visits as they happen rather than after batch processing. Most useful for launches and incidents — the moments when yesterday's data isn't good enough.",
    link: {
      label: "How to see live traffic on your website",
      to: "/blog/live-website-traffic",
    },
  },
  {
    id: "referral-traffic",
    term: "Referral traffic",
    definition:
      "Visits that arrive through a link on another website — a mention in an article, a directory listing, a forum post. The referrer header tells your analytics which site sent them.",
    link: {
      label: "Traffic sources: direct vs. organic vs. referral vs. paid",
      to: "/blog/traffic-sources-explained",
    },
  },
  {
    id: "retention-rate",
    term: "Retention rate",
    definition:
      "The share of customers or users still active after a given period — the complement of churn. Read through cohorts, it's the clearest available signal of whether a product keeps its promises.",
    link: {
      label: "Churn Rate vs Retention Rate",
      to: "/metrics/churn-rate-vs-retention-rate",
    },
  },

  // S
  {
    id: "sampling",
    term: "Sampling",
    definition:
      "Computing reports from a subset of the data instead of all of it, which high-volume tools do to keep queries fast. Sampled numbers wobble — one reason two tools looking at the same site rarely agree exactly.",
    link: {
      label: "Why do different analytics tools show different numbers?",
      to: "/blog/why-analytics-tools-show-different-numbers",
    },
  },
  {
    id: "scroll-depth",
    term: "Scroll depth",
    definition:
      "How far down a page a visitor gets, usually recorded as a percentage of the page's height. Tracked as events at thresholds (25%, 50%, 75%) or continuously, it shows where the page effectively ends for most readers.",
    link: {
      label: "Scroll depth: what it tells you and how to track it",
      to: "/blog/scroll-depth-tracking",
    },
  },
  {
    id: "scroll-map",
    term: "Scroll map",
    definition:
      "A heatmap variant showing how far down the page visitors scroll before stopping. It reveals where attention actually runs out — often far above the content you most wanted seen.",
    link: {
      label: "What is a website heatmap? The four types, explained",
      to: "/blog/what-is-a-website-heatmap",
    },
  },
  {
    id: "segmentation",
    term: "Segmentation",
    definition:
      "Splitting a metric by an attribute — traffic source, device, country, plan — instead of reading the blended average. Averages hide problems; a conversion rate that looks fine overall can be collapsing on mobile.",
  },
  {
    id: "server-side-tracking",
    term: "Server-side tracking",
    definition:
      "Collecting analytics from your own server instead of (or alongside) a script in the browser. It sees traffic that ad blockers hide, at the cost of more setup and less visibility into in-browser behavior.",
    link: {
      label: "Server-side vs client-side tracking, explained without jargon",
      to: "/blog/server-side-vs-client-side",
    },
  },
  {
    id: "session",
    term: "Session",
    definition:
      "One visit: a series of interactions by the same visitor, usually ended by 30 minutes of inactivity. Every tool draws the boundaries a little differently, which is why session counts never quite match between tools.",
    link: {
      label: "What counts as a “session”? Every tool defines it differently",
      to: "/blog/what-counts-as-a-session",
    },
  },
  {
    id: "session-replay",
    term: "Session replay",
    definition:
      'A reconstruction of an individual visit you can watch like a video — every scroll, click, and hesitation. It\'s how you find out what "users drop off at step three" actually looks like.',
    link: {
      label: "Session replay, explained (with examples)",
      to: "/blog/session-replay-explained",
    },
    productLink: {
      label: "Session replays in Bigdelta",
      to: "/session-replays",
    },
  },

  // T
  {
    id: "tag-manager",
    term: "Tag manager",
    definition:
      "A tool for deploying and updating tracking scripts through a web interface instead of code changes. Marketing teams get to add tags without waiting on an engineering release.",
  },
  {
    id: "time-on-page",
    term: "Time on page",
    definition:
      "How long a visitor spent on one page, calculated as the gap between arriving there and arriving at the next page. The last page of a visit has no next page, so its time is typically unmeasured.",
    link: {
      label: "Average session duration is lying to you",
      to: "/blog/average-session-duration",
    },
  },
  {
    id: "tracking-pixel",
    term: "Tracking pixel",
    definition:
      "A tiny, usually invisible image whose loading records an event — classically used to detect email opens and ad views. The request for the image is the tracking; the image itself is beside the point.",
  },
  {
    id: "traffic-analysis",
    term: "Traffic analysis",
    definition:
      "Splitting a traffic total by time, source, page, device or visitor type until it explains something — the step after checking the numbers, where findings actually come from.",
    link: {
      label: "How to analyze website traffic",
      to: "/blog/website-traffic-analysis",
    },
  },
  {
    id: "traffic-source",
    term: "Traffic source",
    definition:
      "Where a visit came from, grouped into channels: organic search, paid, referral, social, email, direct. Source data is only as good as the referrer and tagging behind it — which is why direct is always overstated.",
    link: {
      label: "Traffic sources: direct vs. organic vs. referral vs. paid",
      to: "/blog/traffic-sources-explained",
    },
  },
  {
    id: "trial-to-paid-conversion-rate",
    term: "Trial-to-paid conversion rate",
    definition:
      "The share of free-trial users who become paying customers. Alongside activation rate, it's the number that tells you whether the product sells itself once people are inside.",
    link: {
      label: "Trial-to-Paid Conversion Rate",
      to: "/metrics/trial-to-paid-conversion-rate",
    },
  },

  // U
  {
    id: "unique-visitor",
    term: "Unique visitor",
    definition:
      "One person counted once within a reporting period, no matter how many times they came back. In practice tools count unique browsers, not people — the same person on a laptop and a phone is two uniques.",
    link: {
      label: "Page views vs. visits vs. unique visitors",
      to: "/blog/visitors-vs-visits-vs-pageviews",
    },
  },
  {
    id: "user-journey",
    term: "User journey",
    definition:
      "The full path a person takes from first hearing about you to converting and beyond, across visits, channels, and devices. Individual reports each capture a fragment of it; stitching the fragments together is the hard part.",
  },
  {
    id: "utm-parameters",
    term: "UTM parameters",
    definition:
      'Tags appended to a URL — utm_source, utm_medium, utm_campaign — that tell analytics exactly where a visit came from. They\'re the difference between "traffic went up" and "the March newsletter drove 400 visits."',
    link: { label: "UTM parameters: a practical guide", to: "/blog/utm-parameters-guide" },
  },

  // V
  {
    id: "vanity-metric",
    term: "Vanity metric",
    definition:
      "A number that trends up and feels good but doesn't inform any decision — cumulative signups, total page views, follower counts. The test: if the number doubled tomorrow, would you do anything differently?",
  },
  {
    id: "visitor-tracking",
    term: "Visitor tracking",
    definition:
      "Counting and describing the people on your own site in aggregate — via an analytics snippet, a cookieless counter, or server logs. Distinct from identifying individual visitors, which anonymous traffic doesn't allow.",
    link: {
      label: "How to track website visitors",
      to: "/blog/track-website-visitors",
    },
  },

  // W
  {
    id: "web-analytics",
    term: "Web analytics",
    definition:
      "Measuring what happens on a website — who visits, where they come from, what they do, and whether they convert — to make better decisions about it. Everything else in this glossary hangs off that.",
    link: {
      label: "What is web analytics? A plain-English guide",
      to: "/blog/what-is-web-analytics",
    },
  },
  {
    id: "website-analytics",
    term: "Website analytics",
    definition:
      "Another name for web analytics: counting a site's visitors, where they came from, and what they did there. Tools use whichever label suits them — same dashboards, same numbers, different word on the box.",
    link: {
      label: "What is web analytics? A plain-English guide",
      to: "/blog/what-is-web-analytics",
    },
  },
];

const byTerm = (a: GlossaryTerm, b: GlossaryTerm): number =>
  a.term.localeCompare(b.term, "en", { sensitivity: "base" });

/** Terms grouped by first letter, both levels sorted — the page's render model. */
export const GLOSSARY_GROUPS: GlossaryLetterGroup[] = [...TERMS]
  .sort(byTerm)
  .reduce<GlossaryLetterGroup[]>((groups, term) => {
    const letter = term.term[0].toUpperCase();
    const last = groups[groups.length - 1];
    if (last && last.letter === letter) {
      return [...groups.slice(0, -1), { ...last, terms: [...last.terms, term] }];
    }
    return [...groups, { letter, terms: [term] }];
  }, []);

export const GLOSSARY_TERM_COUNT: number = TERMS.length;

/** A–Z with a flag for letters that have entries, for the jump bar. */
export const GLOSSARY_LETTERS: { letter: string; hasTerms: boolean }[] = Array.from(
  { length: 26 },
  (_, i) => {
    const letter = String.fromCharCode(65 + i);
    return { letter, hasTerms: GLOSSARY_GROUPS.some((g) => g.letter === letter) };
  },
);
